import { getTodayNormalized, normalizeToStartOfDay, isSameDay } from './dateUtils.js';
import { getDatesToChapters } from '../components/challenges/challenges.js';
import { getReadingPortionsFromBooks } from './bible_utils.js';
import { parseDateList } from './dateUtils.js';

/**
 * Scrolls to the top of the page.
 * @returns {void}
 */
export function scrollToTop() {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
}

/**
 * Check if a user has completed today's reading for a specific challenge.
 * @param {Object} challenge - Challenge object with schedule data
 * @param {string} progress - User's progress string (1s and 0s)
 * @returns {boolean} True if today's reading is complete, false otherwise
 */
export function hasCompletedTodaysReading(challenge, progress) {
  if (!challenge || !progress) {
    return false;
  }

  try {
    // Get the challenge schedule
    const readingPortions = getReadingPortionsFromBooks(challenge.books);
    const datesToSkip = parseDateList(challenge.skip_dates || '');
    const datesToChapters = getDatesToChapters(
      challenge.start_date,
      challenge.end_date,
      challenge.days,
      readingPortions,
      datesToSkip
    );

    if (!datesToChapters || datesToChapters.length === 0) {
      return false;
    }

    // Find today's index in the schedule
    const today = getTodayNormalized();
    let todayIndex = -1;

    for (let i = 0; i < datesToChapters.length; i++) {
      const challengeDate = datesToChapters[i][0];
      const normalizedChallengeDate = normalizeToStartOfDay(challengeDate);
      
      if (isSameDay(today, normalizedChallengeDate)) {
        todayIndex = i;
        break;
      }
    }

    // If today is not a reading day, consider it "complete"
    if (todayIndex === -1) {
      return true;
    }

    // Check if the progress string indicates completion for today
    return progress[todayIndex] === '1';
  } catch (error) {
    console.error('Error checking today\'s reading completion:', error);
    return false;
  }
}

export function shouldShowCooperativeStreak(datesToChapters, progressString, referenceDate) {
  if (!Array.isArray(datesToChapters) || datesToChapters.length === 0 || typeof progressString !== 'string' || !referenceDate) {
    return false;
  }

  try {
    const today = normalizeToStartOfDay(referenceDate);
    const todayIndex = datesToChapters.findIndex((dateToChapter) =>
      isSameDay(normalizeToStartOfDay(dateToChapter[0]), today)
    );

    let closestPreviousIndex = -1;
    datesToChapters.forEach((dateToChapter, index) => {
      const normalizedDate = normalizeToStartOfDay(dateToChapter[0]);
      if (normalizedDate < today) {
        closestPreviousIndex = index;
      }
    });

    const readToday = todayIndex >= 0 && progressString[todayIndex] === '1';
    const readClosestPrevious = closestPreviousIndex >= 0 && progressString[closestPreviousIndex] === '1';
    return readToday || readClosestPrevious;
  } catch {
    return false;
  }
}

/**
 * Check if any challenges have incomplete readings for today.
 * @param {Array} challenges - Array of challenge objects with progress
 * @returns {boolean} True if any challenge has incomplete reading for today
 */
export function hasAnyIncompleteReadingsToday(challenges) {
  if (!Array.isArray(challenges) || challenges.length === 0) {
    return false;
  }

  return challenges.some(challenge => {
    return !hasCompletedTodaysReading(challenge, challenge.progress);
  });
}
